import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";

const durationOptions = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last 7 days", label: "Last 7 Days" },
  { value: "last 30 days", label: "Last 30 Days" },
  { value: "last 6 months", label: "Last 6 Months" },
  { value: "last 1 year", label: "Last 1 Year" },
  { value: "custom", label: "Custom Date Range" },
];

const formatDateToString = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DateRangeSelector = ({ dateOptions, onChange, sx }) => {
  const [showCalendar, setShowCalendar] = useState(false);

  const handleDurationChange = (e) => {
    const newDuration = e.target.value;

    if (newDuration === "custom") {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
    }

    onChange({
      ...dateOptions,
      duration: newDuration,
      customDateRange:
        newDuration === "custom"
          ? dateOptions.customDateRange
          : { startDate: "", endDate: "" },
    });
  };

  const handleDropdownOpen = () => {
    if (dateOptions.duration === "custom" && !showCalendar) {
      setShowCalendar(true);
    }
  };

  const handleStartDateChange = (date) => {
    const startDate = formatDateToString(date);
    onChange({
      ...dateOptions,
      customDateRange: {
        ...dateOptions.customDateRange,
        startDate: startDate,
      },
    });
  };

  const handleEndDateChange = (date) => {
    const endDate = formatDateToString(date);
    const newDateOptions = {
      ...dateOptions,
      customDateRange: {
        ...dateOptions.customDateRange,
        endDate: endDate,
      },
    };

    onChange(newDateOptions);

    if (dateOptions.customDateRange?.startDate && endDate) {
      setShowCalendar(false);
    }
  };

  const handleClearCustomDates = (e) => {
    e.stopPropagation();
    onChange({
      ...dateOptions,
      duration: "today",
      customDateRange: { startDate: "", endDate: "" },
    });
    setShowCalendar(false);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSelectDisplayValue = () => {
    if (dateOptions.duration === "custom") {
      const { startDate, endDate } = dateOptions.customDateRange || {};
      if (startDate && endDate) {
        return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
          endDate
        )}`;
      } else if (startDate) {
        return `From: ${formatDateForDisplay(startDate)}`;
      }
      return "custom";
    }
    return dateOptions.duration;
  };

  const getSelectDisplayText = () => {
    if (dateOptions.duration === "custom") {
      const { startDate, endDate } = dateOptions.customDateRange || {};
      if (startDate && endDate) {
        return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
          endDate
        )}`;
      } else if (startDate) {
        return `From: ${formatDateForDisplay(startDate)}`;
      }
      return "Custom Date Range";
    }
    const option = durationOptions.find(
      (opt) => opt.value === dateOptions.duration
    );
    return option ? option.label : dateOptions.duration;
  };

  const startDateObj = dateOptions.customDateRange?.startDate
    ? new Date(dateOptions.customDateRange.startDate + "T00:00:00")
    : null;
  const endDateObj = dateOptions.customDateRange?.endDate
    ? new Date(dateOptions.customDateRange.endDate + "T00:00:00")
    : null;

  const hasCustomDates =
    dateOptions.duration === "custom" &&
    dateOptions.customDateRange?.startDate &&
    dateOptions.customDateRange?.endDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: "relative",
          ...sx,
        }}
      >
        <FormControl sx={{ width: { xs: "100%", sm: "250px" } }}>
          <Select
            value={getSelectDisplayValue()}
            onChange={handleDurationChange}
            onOpen={handleDropdownOpen}
            displayEmpty
            renderValue={() => getSelectDisplayText()}
            startAdornment={
              hasCustomDates && (
                <IconButton
                  size="small"
                  onClick={handleClearCustomDates}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      color: "#f44336",
                      backgroundColor: "rgba(244, 67, 54, 0.04)",
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{
              background: "#FFFFFF",
              borderRadius: "12px",
              height: "46px",
              width: "100%",
              border: "1px solid #183059",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: hasCustomDates ? "0px" : "14px",
                paddingRight: "32px",
              },
              "& fieldset": {
                border: "none",
              },
            }}
          >
            {durationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {dateOptions.duration === "custom" && showCalendar && (
          <Box
            sx={{
              position: "fixed", // Changed from absolute to fixed
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1300,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 2 },
              p: { xs: 1, md: 2 },
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              width: { xs: "95vw", sm: "90vw", md: "600px" },
              maxWidth: "600px",
              maxHeight: { xs: "90vh", md: "auto" },
              overflowY: { xs: "auto", md: "visible" },
            }}
          >
            <IconButton
              onClick={() => setShowCalendar(false)}
              sx={{
                position: "absolute",
                top: { xs: 4, md: 8 },
                right: { xs: 4, md: 8 },
                zIndex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  color: "#f44336",
                },
              }}
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  textAlign: "center",
                  color: "#1976d2",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                }}
              >
                From Date
              </Typography>
              <StaticDatePicker
                value={startDateObj}
                onChange={handleStartDateChange}
                maxDate={endDateObj || undefined}
                slotProps={{
                  actionBar: { actions: [] },
                }}
                sx={{
                  "& .MuiPickersCalendarHeader-root": {
                    paddingLeft: { xs: 0.5, md: 1 },
                    paddingRight: { xs: 0.5, md: 1 },
                    fontSize: { xs: "0.75rem", md: "1rem" },
                  },
                  "& .MuiDayCalendar-root": {
                    width: "100%",
                    minHeight: { xs: "200px", md: "auto" },
                  },
                  "& .MuiPickersDay-root": {
                    fontSize: { xs: "0.65rem", md: "0.875rem" },
                    width: { xs: "28px", md: "36px" },
                    height: { xs: "28px", md: "36px" },
                  },
                  "& .MuiPickersCalendarHeader-switchViewButton": {
                    fontSize: { xs: "0.65rem", md: "0.875rem" },
                  },
                  "& .MuiIconButton-root": {
                    padding: { xs: "4px", md: "8px" },
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  textAlign: "center",
                  color: startDateObj ? "#4caf50" : "#666",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                }}
              >
                To Date
              </Typography>
              <StaticDatePicker
                value={endDateObj}
                onChange={handleEndDateChange}
                minDate={startDateObj || undefined}
                disabled={!startDateObj}
                slotProps={{
                  actionBar: { actions: [] },
                }}
                sx={{
                  opacity: !startDateObj ? 0.5 : 1,
                  "& .MuiPickersCalendarHeader-root": {
                    paddingLeft: { xs: 0.5, md: 1 },
                    paddingRight: { xs: 0.5, md: 1 },
                    fontSize: { xs: "0.75rem", md: "1rem" },
                  },
                  "& .MuiDayCalendar-root": {
                    width: "100%",
                    minHeight: { xs: "200px", md: "auto" },
                  },
                  "& .MuiPickersDay-root": {
                    fontSize: { xs: "0.65rem", md: "0.875rem" },
                    width: { xs: "28px", md: "36px" },
                    height: { xs: "28px", md: "36px" },
                  },
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#4caf50",
                  },
                  "& .MuiPickersCalendarHeader-switchViewButton": {
                    fontSize: { xs: "0.65rem", md: "0.875rem" },
                  },
                  "& .MuiIconButton-root": {
                    padding: { xs: "4px", md: "8px" },
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;